import {
  StudentInteractionTypes,
  trackStudentInteraction,
} from "@/utils/mixpanel-utils";
import { clearPreviousBiteConfirmationPreference } from "@/utils/confirmationPreferences";
import { useAssessmentStore } from "@/features/tutor/store/useAssessmentStore";
import { useUIStore } from "@/features/tutor/store/useUIStore";
import { useSessionStore } from "@/features/tutor/store/useSessionStore";
import { useSlideStore } from "@/features/tutor/store/useSlideStore";
import { captureAndUploadSlideScreenshot } from "./slideScreenshot";
import { useNotificationStore } from "@/store/useNotificationStore";
import { persistNotepadCheckpoint } from "@/features/notepad/components/canvasApi";
import { stopLessonSession } from "./stopLessonSession";
import { useDebugStore } from "@/store/useDebugStore";
import { resolveLessonCompleteVariant } from "./lessonCompletion";

// Cap on how long a graceful exit waits for server-side cleanup before
// navigating anyway. Cleanup is normally fast; the guard prevents a slow
// teardown from ever hanging the exit. The Lambda remains the eventual writer.
const STOP_LESSON_WAIT_MS = 1500;

export const captureLessonEndGuard = () => {
  const generation = useUIStore.getState().lessonEndGeneration;
  const sessionId = useSessionStore.getState().sessionId;
  const lessonId = useSlideStore.getState().currentLessonId;
  return () =>
    useUIStore.getState().lessonEndGeneration === generation &&
    !useUIStore.getState().lessonExitCommitted &&
    useSessionStore.getState().sessionId === sessionId &&
    useSlideStore.getState().currentLessonId === lessonId;
};

/**
 * Clears preferences, navigates away from the lesson, and tracks the interaction.
 * This is the terminal action — call it when the exit decision has already been made.
 *
 * Awaits stop_lesson (bounded by STOP_LESSON_WAIT_MS) BEFORE navigating so the
 * lessons list reads the just-written bite status instead of racing the
 * server-side cleanup. Callers need not await this.
 *
 * @param {Function} navigate - React Router navigate function
 * @param {URLSearchParams} searchParams - URL search params to get classroomId
 */
export const exitLesson = async (navigate, searchParams) => {
  const replace = useUIStore.getState().lessonLeaveGuardArmed;
  useUIStore.getState().commitLessonExit();
  const generation = useUIStore.getState().lessonEndGeneration;
  clearPreviousBiteConfirmationPreference();
  await Promise.race([
    stopLessonSession(),
    new Promise((resolve) => setTimeout(resolve, STOP_LESSON_WAIT_MS)),
  ]);
  if (useUIStore.getState().lessonEndGeneration !== generation) return;
  const classroomId = searchParams.get("classroomId");
  if (classroomId) {
    navigate(`/classroom/${classroomId}`, { replace });
  } else {
    navigate("/", { replace });
  }
  trackStudentInteraction({
    type: StudentInteractionTypes.END_LESSON,
  });
};

export const skipToExitTicket = async (
  navigate,
  searchParams,
  lessonId,
  isCurrentLesson = captureLessonEndGuard(),
) => {
  if (!isCurrentLesson()) return;
  await captureAndUploadSlideScreenshot();
  if (!isCurrentLesson()) return;
  const store = useAssessmentStore.getState();
  try {
    if (lessonId) {
      await store.fetchAssessment(
        "exit_ticket",
        Number(lessonId),
        isCurrentLesson,
      );
    }
    if (!isCurrentLesson()) return;
    await store.startAttemptRestoringResponses("exit_ticket", isCurrentLesson);
    if (!isCurrentLesson()) return;
    // Mount the survey only after savedResponses is set.
    store.setIsActive("exit_ticket", true);
  } catch (err) {
    if (!isCurrentLesson()) return;
    console.error("Failed to start exit_ticket:", err);
    store.setIsActive("exit_ticket", false);
    exitLesson(navigate, searchParams);
  }
};

export const openLessonCompleteModal = () => {
  const ui = useUIStore.getState();
  if (ui.lessonExitCommitted) return false;
  if (ui.showLessonCompleteModal) return false;
  const variant = resolveLessonCompleteVariant(
    useDebugStore.getState().lessonCompleteOverride,
  );
  ui.openLessonComplete(variant);
  return true;
};

// Input:  navigate, searchParams — router handles
//         opts.skipModalForExitTicket — when true (AI-triggered end_lesson),
//           skip the confirmation modal and go directly to the survey.
//         opts.lessonId — required when skipModalForExitTicket is true so
//           fetchAssessment can re-fetch if needed.
// Output: void
// Mutates: UIStore (showExitLessonModal / showLessonCompleteModal), AssessmentStore via skipToExitTicket
export const handleLessonEnd = async (
  navigate,
  searchParams,
  { skipModalForExitTicket = false, lessonId = null } = {},
) => {
  const { showExitLessonModal, showLessonCompleteModal, lessonExitCommitted } =
    useUIStore.getState();
  if (lessonExitCommitted) return;
  if (showExitLessonModal) return;
  // Guard the completion path too: a duplicate end_lesson signal would otherwise
  // re-run captureAndUploadSlideScreenshot() below while the modal is already up.
  if (showLessonCompleteModal) return;
  // Checkpoint/screenshot work can finish after navigation. Its completion
  // belongs to this lesson, never to the next player sharing these stores.
  const isCurrentLesson = captureLessonEndGuard();
  const hasExitTicket =
    useAssessmentStore.getState().assessments.exit_ticket?.has;
  useNotificationStore.getState().cancelAllNotifications();
  // Notepad-history checkpoint (practice notepad bites) before the session
  // winds down — the backend's final snapshot reads Redis. Bounded (2s),
  // non-fatal, no-op for non-notepad slides.
  await persistNotepadCheckpoint();
  if (!isCurrentLesson()) return;
  if (hasExitTicket) {
    if (skipModalForExitTicket) {
      await skipToExitTicket(navigate, searchParams, lessonId, isCurrentLesson);
    } else {
      useUIStore.getState().setShowExitLessonModal(true);
    }
  } else {
    await captureAndUploadSlideScreenshot();
    if (!isCurrentLesson()) return;
    openLessonCompleteModal();
  }
};
